import type React from "react";
import {
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
  startTransition,
} from "react";
import { useBufferedBounds } from "#app/lib/hooks/use-buffered-bounds";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Box,
  useIsMobile,
  useMediaQuery,
  toColor,
  useTheme,
} from "@salestrak/ui";

import { MapOverlayControls } from "#app/components/map/map-overlay-controls";
import { MapInfoboxSheet } from "#app/components/map/map-infobox-sheet";
import type { MapPin } from "#app/components/map/map-view";
import {
  NativeMapView,
  type Region,
  type MapViewRef,
} from "#app/components/map/map-view";
import {
  DEFAULT_MAP_SETTINGS,
  zoomToLatitudeDelta,
  type MapSettings,
} from "#app/components/map/map-styles";
import { useMapInfoboxData } from "#app/lib/hooks/use-map-infobox-data";
import { useIssueView } from "#app/components/issue-view";
import { useAppData } from "#app/lib/data";
import { useCurrentUserFilterToggle } from "#app/lib/hooks/use-current-user-filter-toggle";
import { useMapClusters } from "#app/lib/hooks/use-map-clusters";
import { getEntityGlyph } from "#app/lib/entity-visuals";
import { useUserLocation } from "#app/lib/hooks/use-user-location";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import {
  FOCUS_ZOOM,
  parseFocusCoords,
  pickInitialView,
  regionToView,
  viewToRegion,
  type PersistedMapView,
} from "#app/lib/map";
import { useFilters } from "#app/lib/hooks/use-filters";
import { issueFilterConfig } from "#app/lib/filters/configs/issue-filter-config";
import { FilterPanel } from "#app/components/filters/filter-panel";
import { useCreateMapPin } from "#app/lib/hooks/use-create-map-pin";
import { usePendingPinSearch } from "#app/lib/hooks/use-pending-pin-search";
import { useDrawerWidth } from "#app/components/issue-view/use-drawer-width";
import type { Omittable } from "@salestrak/ui";

// True when the where tree already carries an explicit `archived` predicate
// (the "More Options" chip). Walks AND/OR branches so a nested archived
// condition still counts — mirrors the recursive scan the map service runs
// server-side. When false the map injects `archived.eq=false` to hide
// archived issues by default.
function hasArchivedPredicate(
  where: Record<string, unknown> | undefined,
): boolean {
  if (!where) return false;
  if ("archived" in where) return true;
  for (const key of ["AND", "OR"] as const) {
    const branch = where[key];
    if (
      Array.isArray(branch) &&
      branch.some((sub) =>
        hasArchivedPredicate(sub as Record<string, unknown>),
      )
    )
      return true;
  }
  return false;
}

// ─── BufferedMapView ─────────────────────────────────────────────────────
// Owns displayedPins/displayedClusters state so that marker-buffer flushes
// don't re-render the parent MapScreen (which holds infobox state).

type BufferedMapViewProps = Omittable<{
  rawPins: MapPin[];
  rawClusters: ReturnType<typeof useMapClusters>["clusters"];
  loading: boolean;
  selectedPin: MapPin | null;
  onSelectedPinClear: () => void;
  onRegionChange: (region: Region) => void;
  isDark: boolean;
  mapSettings: MapSettings;
  isMobile: boolean;
  userLocation: { latitude: number; longitude: number } | null;
  onClusterPress: (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => void;
  onPinPress: (pin: MapPin) => void;
  onInfoboxClose: () => void;
  onAddProject: (pin: MapPin) => void;
  onMapClick?: (location: { latitude: number; longitude: number }) => void;
  initialRegion?: Region;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<MapViewRef>;
}>;

function BufferedMapView({
  rawPins,
  rawClusters,
  loading,
  selectedPin,
  onSelectedPinClear,
  onRegionChange,
  isDark,
  mapSettings,
  isMobile,
  userLocation,
  onClusterPress,
  onPinPress,
  onInfoboxClose,
  onAddProject,
  onMapClick,
  initialRegion,
  ref,
}: BufferedMapViewProps) {
    // ─── Buffered rendering ─────────────────────────────────────────────
    const isMapIdleRef = useRef(true);
    const latestPinsRef = useRef(rawPins);
    const latestClustersRef = useRef(rawClusters);
    latestPinsRef.current = rawPins;
    latestClustersRef.current = rawClusters;

    const [displayedPins, setDisplayedPins] = useState(rawPins);
    const [displayedClusters, setDisplayedClusters] = useState(rawClusters);

    useEffect(() => {
      if (isMapIdleRef.current) {
        startTransition(() => {
          setDisplayedPins(rawPins);
          setDisplayedClusters(rawClusters);
        });
      }
    }, [rawPins, rawClusters]);

    // Keep the selected pin rendered even if the latest clustering result has
    // folded it into a cluster (happens when tapping an edge pin pans the map
    // and the new viewport reclusters its grid cell).
    const pins: MapPin[] = useMemo(() => {
      if (!selectedPin || displayedPins.some((p) => p.id === selectedPin.id)) {
        return displayedPins;
      }
      return [...displayedPins, selectedPin];
    }, [displayedPins, selectedPin]);

    useEffect(() => {
      if (!selectedPin) return;
      // Don't clear locally-created pins (from map clicks) — they aren't in the GraphQL data
      if (selectedPin.isLocal) return;
      // Don't clear while a re-query is in flight — preserveIds triggers a
      // re-query on selection; clearing during that window would cause an
      // oscillation loop (clear → preserveIds undefined → re-query → clear).
      if (loading) return;
      // Check rawPins (source of truth), not displayedPins — the buffered
      // display may lag behind, and clearing based on stale display data
      // would immediately remove a pin that was just selected via search.
      if (rawPins.some((pin) => pin.id === selectedPin.id)) return;
      // If the pin's coords now fall inside a cluster's bounds, it got
      // reclustered by the pan — don't close the infobox. This preserves
      // the "select edge pin → map pans → pin reclusters" case.
      // Note: cluster geometry is not recomputed after preserved-item extraction,
      // so this check is still needed even with backend preserveIds support.
      const insideCluster = rawClusters.some(
        (c) =>
          c.bounds.south <= selectedPin.latitude &&
          c.bounds.north >= selectedPin.latitude &&
          c.bounds.west <= selectedPin.longitude &&
          c.bounds.east >= selectedPin.longitude,
      );
      if (insideCluster) return;
      onSelectedPinClear();
    }, [rawPins, rawClusters, selectedPin, onSelectedPinClear, loading]);

    // ─── Interaction handlers ───────────────────────────────────────────
    const handleInteractionStart = useCallback(() => {
      isMapIdleRef.current = false;
    }, []);

    const onRegionChangeRef = useRef(onRegionChange);
    onRegionChangeRef.current = onRegionChange;

    const handleRegionChange = useCallback((newRegion: Region) => {
      isMapIdleRef.current = true;

      startTransition(() => {
        setDisplayedPins(latestPinsRef.current);
        setDisplayedClusters(latestClustersRef.current);
      });

      onRegionChangeRef.current(newRegion);
    }, []);

    return (
      <NativeMapView
        ref={ref}
        isDark={isDark}
        mapSettings={mapSettings}
        pins={pins}
        clusters={displayedClusters}
        userLocation={userLocation}
        initialRegion={initialRegion}
        onRegionChangeComplete={handleRegionChange}
        onInteractionStart={handleInteractionStart}
        onClusterPress={onClusterPress}
        onPinPress={onPinPress}
        selectedPin={isMobile ? null : selectedPin}
        onInfoboxClose={onInfoboxClose}
        onAddProject={onAddProject}
        onMapClick={onMapClick}
      />
    );
}

// ─── MapScreen ───────────────────────────────────────────────────────────

function MapScreen() {
  const navigate = useNavigate();
  const mapRef = useRef<MapViewRef>(null);
  const { workflows } = useAppData();
  const { openIssue, openIssueById, openCreateIssue } = useIssueView();
  // The Google Maps style array is JS, not CSS, so it needs the RESOLVED
  // scheme. The library `useTheme()` reports the stored mode ("system" included), so
  // resolve "system" against the OS query here.
  const { theme } = useTheme();
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const isDark = theme === "dark" || (theme === "system" && prefersDark);
  const isMobile = useIsMobile();

  // Filter state
  const filters = useFilters(issueFilterConfig);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);

  const { workspace } = Route.useParams();
  const search = Route.useSearch();
  const urlParams = { workspace, ...search };

  // Remembered last view — survives nav-away/back. Scoped per workspace so a
  // workspace in one region doesn't bleed its last position into another.
  // Seeded synchronously from localStorage on first render so pickInitialView
  // below sees it without a flicker.
  const { state: lastView, update: setLastView } =
    usePersistedState<PersistedMapView>(
      `map-last-view:${urlParams.workspace ?? ""}`,
      { lat: null, lng: null, z: null },
    );

  const initialRegionRef = useRef<Region | undefined>(undefined);
  if (initialRegionRef.current === undefined) {
    const initial = pickInitialView(urlParams, lastView);
    if (initial) initialRegionRef.current = viewToRegion(initial);
  }

  // Buffered bounds — debounced, with 20% over-fetch and containment check
  const { bounds, zoomLevel, handleRegionChange: handleBoundsRegionChange, reset: resetBounds } = useBufferedBounds();

  const handleRegionChange = useCallback(
    (r: Region) => {
      handleBoundsRegionChange(r);
      const view = regionToView(r);
      navigate({
        to: ".",
        search: (prev) => ({
          ...prev,
          lat: String(view.lat),
          lng: String(view.lng),
          z: String(view.z),
        }),
        replace: true,
      });
      // Mirror to localStorage so position survives nav-away/back even when
      // the inbound URL has no params (e.g. clicking Map in the sidebar).
      setLastView(view);
    },
    [handleBoundsRegionChange, setLastView, navigate],
  );

  const { state: mapSettings, update: setMapSettings } = usePersistedState(
    "map-settings",
    DEFAULT_MAP_SETTINGS,
  );

  const { location: userLocation, requestPermission } = useUserLocation(mapSettings.accuracySensors);

  // Selected pin for infobox
  const [selectedPin, setSelectedPin] = useState<MapPin | null>(null);
  // The infobox sheet is a Drawer whose presence IS `selectedPin != null` —
  // there is no imperative present()/dismiss() handle.
  const updateSelectedPin = useCallback(
    (
      updater:
        | MapPin
        | null
        | ((prev: MapPin | null) => MapPin | null),
    ) => {
      setSelectedPin((prev) =>
        typeof updater === "function" ? updater(prev) : updater,
      );
    },
    [],
  );
  const {
    isCurrentUserButtonSelected,
    isCurrentUserFilterApplied,
    handleCurrentUserPress,
    handleCurrentUserClear,
  } = useCurrentUserFilterToggle();

  // Wrap issue filters for the MapClusterWhereInput shape
  const mapWhere = useMemo(() => {
    const issueConditions: Record<string, unknown>[] = [];
    if (filters.where) issueConditions.push(filters.where);
    if (isCurrentUserFilterApplied)
      issueConditions.push({ onlyCurrentUser: true });
    // Default the map to active-only. Unless the user has explicitly set an
    // archived filter via the "More Options" chip (Show only archived /
    // Include archived), exclude archived issues so they don't show by
    // default.
    if (!hasArchivedPredicate(filters.where))
      issueConditions.push({ archived: { eq: false } });
    if (issueConditions.length === 0) return undefined;
    if (issueConditions.length === 1) return { issue: issueConditions[0] };
    return { issue: { AND: issueConditions } };
  }, [filters.where, isCurrentUserFilterApplied]);

  // Collect entity IDs that must not be swallowed by clusters.
  // Serialize to a stable key so the array ref doesn't change when
  // selectedPin is replaced by a new object with the same issueIds.
  const preserveIdsKey = selectedPin && !selectedPin.isLocal && selectedPin.issueIds.length > 0
    ? selectedPin.issueIds.join(",")
    : "";
  const preserveIds = useMemo(
    () => (preserveIdsKey ? preserveIdsKey.split(",") : undefined),
    [preserveIdsKey],
  );

  // Live data from GraphQL (with filter where clause)
  const { pins: rawPins, clusters: rawClusters, loading: mapLoading } = useMapClusters(
    bounds,
    zoomLevel,
    mapWhere,
    preserveIds,
  );

  // Reverse geocode + findOrCreateAddress for map clicks
  const { createPin, cancel: cancelCreatePin } = useCreateMapPin();

  // Fetch infobox data at screen level (the Drawer portals outside IssueViewProvider)
  const {
    issues: infoboxIssues,
    addressDetails: infoboxAddress,
  } = useMapInfoboxData(selectedPin);

  // Once GraphQL delivers a server pin containing the same issues as the
  // local pin, swap selectedPin to the server version so the local-pin
  // render branch stops and the cleanup effect can manage it normally.
  useEffect(() => {
    if (!selectedPin?.isLocal || selectedPin.issueIds.length === 0) return;
    const serverPin = rawPins.find((p) =>
      selectedPin.issueIds.some((id) => p.issueIds.includes(id)),
    );
    if (serverPin) updateSelectedPin(serverPin);
  }, [rawPins, selectedPin, updateSelectedPin]);

  // ─── Handlers ───────────────────────────────────────────────────────────

  const handleZoomIn = useCallback(() => {
    mapRef.current?.zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    mapRef.current?.zoomOut();
  }, []);

  const handleLocationPress = useCallback(async () => {
    if (!userLocation) {
      await requestPermission();
      return;
    }
    const targetZoom = 16;
    if (zoomLevel < targetZoom - 1) {
      mapRef.current?.animateToRegion(
        {
          latitude: userLocation.latitude,
          longitude: userLocation.longitude,
          latitudeDelta: zoomToLatitudeDelta(targetZoom),
          longitudeDelta: zoomToLatitudeDelta(targetZoom) * 1.5,
        },
        500,
      );
    } else {
      mapRef.current?.panTo({
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
      });
    }
  }, [userLocation, requestPermission, zoomLevel]);

  const drawerWidth = useDrawerWidth();

  const panTo = useCallback(
    (location: { latitude: number; longitude: number }) => {
      const zoom = 16;
      mapRef.current?.animateToRegion(
        {
          latitude: location.latitude,
          longitude: location.longitude,
          latitudeDelta: zoomToLatitudeDelta(zoom),
          longitudeDelta: zoomToLatitudeDelta(zoom) * 1.5,
        },
        500,
      );
    },
    [],
  );

  const { start: startPinSearch, cancel: cancelPinSearch } = usePendingPinSearch({
    pins: rawPins,
    onMatch: (match) => updateSelectedPin(match),
    onFallback: (coords) => {
      createPin(coords.lat, coords.lng).then((pin) => {
        if (pin) updateSelectedPin(pin);
      });
    },
    where: mapWhere,
  });

  const navigateToLocation = useCallback(
    (coords: { latitude: number; longitude: number }, addressId?: string) => {
      panTo(coords);
      updateSelectedPin(null);
      cancelCreatePin();
      resetBounds();
      const zoom = FOCUS_ZOOM;
      handleRegionChange({
        latitude: coords.latitude,
        longitude: coords.longitude,
        latitudeDelta: zoomToLatitudeDelta(zoom),
        longitudeDelta: zoomToLatitudeDelta(zoom) * 1.5,
      });
      startPinSearch({
        lat: coords.latitude,
        lng: coords.longitude,
        addressId,
      });
    },
    [panTo, cancelCreatePin, handleRegionChange, resetBounds, startPinSearch, updateSelectedPin],
  );

  // One-shot focus from an external navigation (e.g. selecting an address in
  // global search): pan to the coords and find-or-create a pin there, popping
  // the infobox.
  //
  // Depends ONLY on the focus params — the two callbacks go through refs.
  // navigateToLocation's identity churns (it closes over handleRegionChange,
  // which closes over usePersistedState's setter), and it WRITES the URL, so
  // with it in the dep array every run scheduled the next one. The params
  // themselves change exactly twice — set by the inbound navigation, cleared
  // below — which is the real "one shot", so no latch ref is needed to enforce
  // it. An earlier version used a ref latch instead and had to choose between
  // looping (re-arm on teardown) and silently skipping the pin search when
  // StrictMode's mount/teardown/replay cancelled it (never re-arm). Narrowing
  // the deps removes the choice.
  const navigateToLocationRef = useRef(navigateToLocation);
  navigateToLocationRef.current = navigateToLocation;
  const focusNavigateRef = useRef(navigate);
  focusNavigateRef.current = navigate;
  // Primitive deps, not the params object: the object is a fresh reference
  // per render, and only these three members arm the one-shot focus.
  const { focusLat, focusLng, addressId: focusAddressId } = urlParams;
  useEffect(() => {
    // Parsed by the same helper that seeds the initial view, so a blank pair
    // (`?focusLat=&focusLng=`) can't mean "no focus" to pickInitialView while
    // meaning the coordinate (0, 0) here.
    const focus = parseFocusCoords({ focusLat, focusLng });
    if (!focus) return;
    // Safe to repeat: navigateToLocation cancels any in-flight pin search before
    // starting a new one, so a StrictMode replay just redoes the lookup.
    navigateToLocationRef.current(
      { latitude: focus.lat, longitude: focus.lng },
      focusAddressId,
    );
    focusNavigateRef.current({
      to: ".",
      search: (prev) => ({
        ...prev,
        focusLat: undefined,
        focusLng: undefined,
        addressId: undefined,
      }),
      replace: true,
    });
  }, [focusLat, focusLng, focusAddressId]);

  // URL → selected pin. When the address-button on an issue dialog pushes
  // /map?issueId=…, seed a local pin so the infobox opens immediately. The
  // existing local-pin → server-pin swap effect upgrades it to the real
  // clustered pin when the map query resolves. Re-seeds when issueId
  // changes; ignores lat/lng churn from handleRegionChange's URL writes.
  const handledIssueIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const id = urlParams.issueId;
    if (!id || handledIssueIdRef.current === id) return;
    const lat = Number(urlParams.lat);
    const lng = Number(urlParams.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    handledIssueIdRef.current = id;
    updateSelectedPin({
      id: urlParams.addressId ?? `pin-${lat},${lng}`,
      latitude: lat,
      longitude: lng,
      color: "grey",
      count: 1,
      addressId: urlParams.addressId,
      issueIds: [id],
      isLocal: true,
    });
    // Seed params are one-shot — clear them so a refresh doesn't replay the
    // selection. lat/lng/z stay (handleRegionChange keeps them in sync with
    // the camera for shareable URLs).
    navigate({
      to: ".",
      search: (prev) => ({ ...prev, issueId: undefined, addressId: undefined }),
      replace: true,
    });
  }, [urlParams.issueId, urlParams.lat, urlParams.lng, urlParams.addressId, updateSelectedPin, navigate]);

  // The invariant: camera = pin shifted left by drawerWidth/2. One effect,
  // two inputs. Drawer opens/closes, drawer drag-resizes, selected pin
  // changes — all funnel through here. MapView's ref queue handles the
  // case where the underlying Google Map isn't ready yet.
  useEffect(() => {
    if (!selectedPin) return;
    mapRef.current?.focusWithOffset(
      { latitude: selectedPin.latitude, longitude: selectedPin.longitude },
      drawerWidth,
    );
  }, [drawerWidth, selectedPin]);

  const handleClusterPress = useCallback(
    (clusterBounds: {
      north: number;
      south: number;
      east: number;
      west: number;
    }) => {
      mapRef.current?.fitToCoordinates(
        [
          { latitude: clusterBounds.north, longitude: clusterBounds.east },
          { latitude: clusterBounds.south, longitude: clusterBounds.west },
        ],
        {
          edgePadding: { top: 50, right: 50, bottom: 50, left: 50 },
          animated: true,
        },
      );
    },
    [],
  );

  const selectedPinRef = useRef(selectedPin);
  selectedPinRef.current = selectedPin;

  // The (selectedPin, drawerWidth) effect owns camera positioning.
  const handlePinPress = useCallback((pin: MapPin) => {
    cancelPinSearch();
    updateSelectedPin(pin);
  }, [cancelPinSearch, updateSelectedPin]);

  const handleInfoboxClose = useCallback(() => {
    updateSelectedPin(null);
    cancelCreatePin();
  }, [cancelCreatePin, updateSelectedPin]);

  const handleMapClick = useCallback(
    (location: { latitude: number; longitude: number }) => {
      cancelPinSearch();

      if (selectedPinRef.current) {
        updateSelectedPin(null);
        cancelCreatePin();
        return;
      }

      // Don't pan to the raw click — wait for createPin to resolve so the
      // (selectedPin, drawerWidth) effect can pan once, to the snapped coords.
      createPin(location.latitude, location.longitude).then((pin) => {
        if (!pin) return;
        // reverseGeocode snapped the click to the address's canonical coords.
        // If a server pin already exists for that address, open it instead of
        // dropping an empty local pin on top of it.
        const existing = rawPins.find(
          (p) =>
            (pin.addressId && p.addressId === pin.addressId) ||
            (p.latitude === pin.latitude && p.longitude === pin.longitude),
        );
        if (existing) {
          updateSelectedPin(existing);
          return;
        }
        updateSelectedPin(pin);
      });
    },
    [createPin, cancelCreatePin, cancelPinSearch, rawPins, updateSelectedPin],
  );

  const handleAddProject = useCallback(
    (pin: MapPin) => {
      const ownerName = pin.propertyDetails?.ownerNames?.[0];
      openCreateIssue({
        source: "map",
        address: pin.wholeAddress
          ? { wholeAddress: pin.wholeAddress, latLng: { lat: pin.latitude, lng: pin.longitude } }
          : undefined,
        name: ownerName || pin.wholeAddress || undefined,
        onCreated: (issue) => {
          const colorKey = toColor(issue.icon?.color) ?? "grey";
          const workflow = workflows.find(
            (w) => w.workflowId === issue.workflowId,
          );
          // Pins without a custom workflow icon stay glyph-less dots — only
          // resolve through entity-visuals when there is an icon to show.
          const workflowIcon = workflow?.icon?.icon
            ? (getEntityGlyph({
                kind: "workflow",
                id: workflow.workflowId,
                workflow,
              }).icon ?? undefined)
            : undefined;
          updateSelectedPin((prev) => {
            if (!prev) return null;
            return {
              ...prev,
              id: issue.issueId,
              count: 1,
              color: colorKey,
              icon: workflowIcon,
              issueIds: [...prev.issueIds, issue.issueId],
            };
          });
        },
      });
    },
    [openCreateIssue, workflows, updateSelectedPin],
  );

  const handleIssuePress = useCallback(
    (issue: Parameters<typeof openIssue>[0]) => openIssue(issue),
    [openIssue],
  );

  const handleActivityPress = useCallback(
    (issue: Parameters<typeof openIssue>[0]) =>
      openIssue(issue, { initialTab: "activity" }),
    [openIssue],
  );

  const handleSheetAddProject = useCallback(() => {
    if (selectedPinRef.current) handleAddProject(selectedPinRef.current);
  }, [handleAddProject]);

  return (
    <>
      {/* Column, not a bare Box: the map surface below claims its height with
          flex-1, which needs a flex parent to grow inside. A block wrapper
          leaves it at content height — i.e. zero, since the map paints into an
          empty div. */}
      <Box direction="column" fill="flex">
        <BufferedMapView
          ref={mapRef}
          rawPins={rawPins}
          rawClusters={rawClusters}
          loading={mapLoading}
          selectedPin={selectedPin}
          onSelectedPinClear={handleInfoboxClose}
          onRegionChange={handleRegionChange}
          isDark={isDark}
          mapSettings={mapSettings}
          isMobile={isMobile}
          userLocation={userLocation}
          initialRegion={initialRegionRef.current}
          onClusterPress={handleClusterPress}
          onPinPress={handlePinPress}
          onInfoboxClose={handleInfoboxClose}
          onAddProject={handleAddProject}
          onMapClick={handleMapClick}
        />

        <MapOverlayControls
          isDark={isDark}
          currentUserSelected={isCurrentUserButtonSelected}
          onCurrentUserClick={handleCurrentUserPress}
          onCurrentUserClear={handleCurrentUserClear}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          zoomLevel={zoomLevel}
          onLocationClick={handleLocationPress}
          mapSettings={mapSettings}
          onMapSettingsChange={setMapSettings}
          filterCount={filters.activeFilterCount}
          onFilterClick={() => setFilterPanelOpen(true)}
          onFilterClear={filters.clearAll}
        />

        {isMobile && (
          <MapInfoboxSheet
            pin={selectedPin}
            issues={infoboxIssues}
            addressDetails={infoboxAddress}
            onDismiss={handleInfoboxClose}
            onIssueClick={handleIssuePress}
            onActivityClick={handleActivityPress}
            onAddProject={handleSheetAddProject}
          />
        )}
      </Box>

      <FilterPanel
        open={filterPanelOpen}
        onOpenChange={setFilterPanelOpen}
        config={issueFilterConfig}
        filters={filters}
      />
    </>
  );
}

export const Route = createFileRoute("/(app)/$workspace/map")({
  validateSearch: (
    search: Record<string, unknown>,
    // A search param is written back as an explicit `undefined` to clear it —
    // the router reads that as "absent from the URL" — so the members relax.
  ): Omittable<{
    lat?: string;
    lng?: string;
    z?: string;
    issueId?: string;
    addressId?: string;
    focusLat?: string;
    focusLng?: string;
  }> => ({
    lat: typeof search.lat === "string" ? search.lat : undefined,
    lng: typeof search.lng === "string" ? search.lng : undefined,
    z: typeof search.z === "string" ? search.z : undefined,
    issueId: typeof search.issueId === "string" ? search.issueId : undefined,
    addressId:
      typeof search.addressId === "string" ? search.addressId : undefined,
    // One-shot focus (e.g. from global-search address results): pan there and
    // find-or-create a pin, popping the infobox.
    focusLat: typeof search.focusLat === "string" ? search.focusLat : undefined,
    focusLng: typeof search.focusLng === "string" ? search.focusLng : undefined,
  }),
  component: MapScreen,
});
